import { useToast } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Util, CreditAgreement } from '@finley-technologies/finley-types';

import {
  errorToastAdminConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import {
  useGetCreditAgreementFullArtifactWipQuery,
  useGetCurrentCreditAgreementFullArtifactQuery,
} from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export const useGetCurrentCaAndWip = ({
  creditFacilityId,
  creditAgreementsPath,
}: {
  creditFacilityId: string;
  creditAgreementsPath: string;
}): {
  creditAgreementFullArtifactWip: Util.Maybe<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>;
  currentCreditAgreementFullArtifact: Util.Maybe<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>;
} => {
  const navigate = useNavigate();
  const toast = useToast();

  const {
    data: getCreditAgreementFullArtifactWipData,
  } = useGetCreditAgreementFullArtifactWipQuery({
    variables: {
      creditFacilityId,
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastAdminConfig.title = `Error loading credit agreement wip.`;
        toast(errorToastAdminConfig);
      }
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: getCurrentCreditAgreementFullArtifactData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: {
      creditFacilityId,
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastAdminConfig.title = `Error loading current credit agreement.`;
        toast(errorToastAdminConfig);
      }
    },
    fetchPolicy: 'network-only',
  });

  const currentCreditAgreementFullArtifact = useMemo(() => {
    const currentCreditAgreementFullArtifactString =
      getCurrentCreditAgreementFullArtifactData
        ?.currentCreditAgreementFullArtifact?.creditAgreement;
    if (currentCreditAgreementFullArtifactString) {
      try {
        return parseCreditAgreementFullArtifact(
          currentCreditAgreementFullArtifactString,
        );
      } catch (error) {
        errorToastAdminConfig.title = `Error parsing credit agreement artifact wip for credit facility ${creditFacilityId}`;
        toast(errorToastAdminConfig);
        navigate(creditAgreementsPath);
        return null;
      }
    }

    return null;
  }, [
    creditFacilityId,
    getCurrentCreditAgreementFullArtifactData
      ?.currentCreditAgreementFullArtifact?.creditAgreement,
    navigate,
    toast,
    creditAgreementsPath,
  ]);

  const creditAgreementFullArtifactWip = useMemo(() => {
    // Return null early if any of the queried data is not available yet
    if (
      !getCreditAgreementFullArtifactWipData ||
      !getCurrentCreditAgreementFullArtifactData
    ) {
      return null;
    }

    // Utilizing existing WIP artifact if available
    const existingWipString =
      getCreditAgreementFullArtifactWipData.creditAgreementFullArtifactWip.wip
        .creditAgreement;

    const errors =
      getCreditAgreementFullArtifactWipData.creditAgreementFullArtifactWip
        .errors;

    if (errors.length) {
      toast({
        position: 'top',
        isClosable: true,
        status: 'error',
        title: `Credit Agreement WIP contained the following errors:\n\n${JSON.stringify(
          errors,
        )}`,
      });
    }
    try {
      return parseCreditAgreementFullArtifact(existingWipString);
    } catch (error) {
      errorToastAdminConfig.title = `Error parsing credit agreement artifact wip for credit facility ${creditFacilityId}`;
      toast(errorToastAdminConfig);
      navigate(creditAgreementsPath);
      return null;
    }
  }, [
    creditFacilityId,
    getCreditAgreementFullArtifactWipData,
    getCurrentCreditAgreementFullArtifactData,
    navigate,
    toast,
    creditAgreementsPath,
  ]);

  return {
    creditAgreementFullArtifactWip,
    currentCreditAgreementFullArtifact,
  };
};
